import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BookingRule } from '../../model/booking-rule.model';
import { environment } from '../../../environments/environment';

interface BookingRuleApiModel {
  booking_Rule_ID: string;
  booking_Definition_ID: string;
  generalLedger_Account_ID: string;
  booking_Percentage: number;
  kind_Of_Booking: string;
  apply_SignChange: boolean;
  book_By_Employment: boolean | null;
  include_Number_In_Booking: string | null;
  apply_CostCenter: boolean | null;
  apply_CostOwner: boolean | null;
  booking_Definition_Name?: string | null;
  generalLedger_Account_Name?: string | null;
  booking_Group_Name?: string | null;
}

interface BookingRuleApiPayload {
  booking_Rule_ID: string;
  booking_Definition_ID: string;
  generalLedger_Account_ID: string;
  booking_Percentage: number;
  kind_Of_Booking: string;
  apply_SignChange: boolean;
  book_By_Employment: boolean | null;
  include_Number_In_Booking: string | null;
  apply_CostCenter: boolean | null;
  apply_CostOwner: boolean | null;
  changeUser: string | null;
  changeDateTime: string;
}

interface AddBookingRuleResponse {
  data?: string;
  booking_Rule_ID?: string;
  bookingRuleId?: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class BookingRuleService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/BookingRule';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all booking rules for the given booking group.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 404 or 204 No Content.
   */
  getByContext(bookingGroupId: string): Observable<BookingRule[]> {
    return this.http
      .get<BookingRuleApiModel[] | null>(
        this.apiUrl + '/ByContext/' + bookingGroupId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiModelToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns all booking rules for the given booking definition.
   * Returns an empty array when the API responds with 404 or 204 No Content.
   */
  getByBookingDefinitionId(bookingDefinitionId: string): Observable<BookingRule[]> {
    return this.http
      .get<BookingRuleApiModel[] | null>(
        this.apiUrl + '/ByBookingDefinitionId/' + bookingDefinitionId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiModelToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns a single booking rule by its id.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns null when the API responds with 404 or 204 No Content.
   */
  getById(id: string): Observable<BookingRule | null> {
    return this.http
      .get<BookingRuleApiModel | null>(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        map((item) => (item ? this.mapApiModelToModel(item) : null)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new booking rule. The id is always forced to the empty guid so the
   * backend can generate a new one.
   */
  addBookingRule(bookingRule: BookingRule): Observable<BookingRule> {
    const payload = this.mapModelToApiPayload({
      ...bookingRule,
      bookingRuleId: EMPTY_GUID,
    });

    return this.http
      .post<AddBookingRuleResponse | null>(this.apiUrl, payload, this.httpOptions)
      .pipe(
        map((response) => {
          const id = this.getCreatedBookingRuleId(response);
          if (id === EMPTY_GUID) {
            // A successful create must return the new id; treat a missing id as a
            // failure so callers don't navigate away from / retry an orphaned record.
            throw new Error('Booking rule create did not return an id.');
          }
          return { ...bookingRule, bookingRuleId: id };
        }),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Updates an existing booking rule.
   */
  updateBookingRule(id: string, bookingRule: BookingRule): Observable<BookingRule> {
    const payload = this.mapModelToApiPayload({
      ...bookingRule,
      bookingRuleId: id,
    });

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(
        map(() => ({ ...bookingRule, bookingRuleId: id })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Removes a booking rule by its id.
   */
  removeBookingRule(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  // ------------------------------------------------------------------------------
  // Helper methods for mapping between API payloads and the BookingRule model.

  private mapApiModelToModel(apiModel: BookingRuleApiModel): BookingRule {
    return {
      bookingRuleId: apiModel.booking_Rule_ID,
      bookingDefinitionId: apiModel.booking_Definition_ID,
      generalLedgerAccountId: apiModel.generalLedger_Account_ID,
      bookingPercentage: apiModel.booking_Percentage,
      kindOfBooking: apiModel.kind_Of_Booking,
      applySignChange: apiModel.apply_SignChange,
      bookByEmployment: apiModel.book_By_Employment,
      includeNumberInBooking: apiModel.include_Number_In_Booking,
      applyCostCenter: apiModel.apply_CostCenter,
      applyCostOwner: apiModel.apply_CostOwner,
      bookingDefinitionName: apiModel.booking_Definition_Name ?? '',
      generalLedgerAccountName: apiModel.generalLedger_Account_Name ?? '',
      bookingGroupName: apiModel.booking_Group_Name ?? '',
    };
  }

  private mapModelToApiPayload(model: BookingRule): BookingRuleApiPayload {
    return {
      booking_Rule_ID: model.bookingRuleId,
      booking_Definition_ID: model.bookingDefinitionId,
      generalLedger_Account_ID: model.generalLedgerAccountId,
      booking_Percentage: model.bookingPercentage,
      kind_Of_Booking: model.kindOfBooking,
      apply_SignChange: model.applySignChange,
      book_By_Employment: model.bookByEmployment,
      include_Number_In_Booking: model.includeNumberInBooking,
      apply_CostCenter: model.applyCostCenter,
      apply_CostOwner: model.applyCostOwner,
      changeUser: null,
      changeDateTime: new Date().toISOString(),
    };
  }

  private getCreatedBookingRuleId(response: AddBookingRuleResponse | null): string {
    return response?.data ?? response?.booking_Rule_ID ?? response?.bookingRuleId ?? EMPTY_GUID;
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
